import React, { createContext, FC, ReactNode, RefObject, useContext } from 'react';
import { useLogger } from '@direct-frontend/modules-logger';
import { getPlatformStyles, isDesktop } from '@direct-frontend/platform-code';

import { Popup } from '../Popup';
import { ClickableIcon } from '../ClickableIcon';
import { IconClose, IconCloseSkinny } from '../../icons/colorless';
import { HStack, VStack } from '../Stack';
import { cn, getClassNames } from '../../lib/css/className/className';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { useInvertedTheme } from '../../lib/theme/ThemeProvider';
import { SkinColor, SkinRole } from '../../lib/skin/getColorSkin/generated/types';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { useStack } from '../../hooks/useStack';
import { ProvideContextPopupsContainer } from '../../lib/layerManager/layerManager';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { VerticalAlignTextPreset } from '../../hooks/useVerticalAlign/types';
import { TextGeneric } from '../TextGeneric/TextGeneric';
import { Text } from '../Text';
import { Image } from '../Image';
import { FloatingButton } from '../FloatingButton';

import { TooltipColor, TooltipProps } from './types';
import { i18n } from './Tooltip.i18n';

getPlatformStyles({
    common: import('./Tooltip.css'),
    desktop: import('./Tooltip@desktop.css'),
    mobile: import('./Tooltip@mobile.css'),
});

const cnTooltip = cn('Tooltip');

const skinMap: Record<TooltipColor, SkinRole | SkinColor> = {
    white: 'gray-neutral',
    warning: 'warning',
    inverted: 'gray-opaque',
    error: 'error',
    info: 'info',
    success: 'success',
};

const mobileCaptionTextSize = '24-32-M + 16-24';
const mobileTextSize = '24-32-M';
function getMobileTextPreset(caption?: ReactNode): VerticalAlignTextPreset {
    return caption ? mobileCaptionTextSize : mobileTextSize;
}

export const Tooltip: FC<TooltipProps> = props => {
    const { LogProvider } = useLogger('Tooltip', props.logNode);

    const {
        hasClose,
        color = 'inverted',
        iconLeft,
        title,
        caption,
        onClose,
        hasTail = true,
        maxWidth = 'auto',
        isModal,
        isDismissable,
        tailClassName,
        customContent,
        hideMobileBottomButton,
        buttons,
        bottomText,
        imageSrc,
        imageAlt,
        ...popupProps
    } = props;

    const invertedTheme = useInvertedTheme();
    const size = 'm';
    const sizeSkinClass = getSizeSkin(size);
    const textSize = 'current-main + current-body';
    const { addonClasses: closeClasses } = useInternalVerticalAlignWithSkin({ textSize, addonType: 'icon' });
    const { addonClasses: iconClasses } = useInternalVerticalAlignWithSkin({ textSize, addonType: 'universal' });

    const closeButtonText = i18n('Закрыть');
    const hasContent = Boolean(props.title) || Boolean(customContent);
    const skinClass = getColorSkin(isDesktop() ? skinMap[color] : 'gray-neutral', {});
    const stackClasses = useStack({});
    const tooltipContainer = useContext(TooltipContainerContext);

    if ((!props.isOpen || !hasContent) && isDesktop()) {
        return null;
    }

    const dataTestId = props['data-testid'] || 'Tooltip';

    const ButtonsStackComponent = isDesktop() ? HStack : VStack;
    const buttonsBlock = buttons ? (
        <ButtonsStackComponent gap={isDesktop() ? '6' : 'current-geometry'} justifyContent="flex-start" flexWrap="wrap">
            {buttons}
        </ButtonsStackComponent>
    ) : null;

    const bottomTextBlock = bottomText && (
        <Text size="s" color="current" role={'supplementary'} className={cnTooltip('bottomText')}>
            {bottomText}
        </Text>
    );

    const hasNonImageCloseButton = hasClose && !imageSrc;

    const contentAddonRight =
        hasNonImageCloseButton && isDesktop() ? (
            <HStack gap={'current-geometry'} className={cnTooltip('contentAddonRight')}>
                {hasNonImageCloseButton && (
                    <ClickableIcon
                        className={cnTooltip('closeButton', [closeClasses])}
                        onClick={onClose}
                        title={closeButtonText}
                        aria-label={closeButtonText}
                        color="current"
                        data-testid={`${dataTestId}.CloseIcon`}
                    >
                        <IconCloseSkinny size={'16'} />
                    </ClickableIcon>
                )}
            </HStack>
        ) : null;

    const ContentStackComponent = isDesktop() ? HStack : VStack;
    const content = (
        <ContentStackComponent alignItems={'flex-start'} gap={isDesktop() ? '8' : '24'}>
            {customContent ? (
                customContent
            ) : (
                <>
                    {iconLeft && (
                        <VStack className={cnTooltip('icon', [isDesktop() ? iconClasses : undefined])}>
                            {iconLeft}
                        </VStack>
                    )}
                    <VStack gap="12">
                        <HStack gap={'8'} alignItems={'flex-start'}>
                            {title &&
                                (isDesktop() ? (
                                    <TextGenericWithSkin
                                        skinSize={size}
                                        textSize={textSize}
                                        caption={caption}
                                        color="current"
                                    >
                                        {title}
                                    </TextGenericWithSkin>
                                ) : (
                                    <TextGeneric
                                        caption={caption}
                                        color="current"
                                        textSize={getMobileTextPreset(caption)}
                                    >
                                        {title}
                                    </TextGeneric>
                                ))}
                            {contentAddonRight}
                        </HStack>
                        {isDesktop() && (buttons || bottomText) && (
                            <HStack className={cnTooltip('bottomContainer')} gap={'16'} justifyContent="space-between">
                                {buttonsBlock}
                                {bottomTextBlock}
                            </HStack>
                        )}
                    </VStack>
                </>
            )}
        </ContentStackComponent>
    );

    const popup = (
        <Popup
            {...popupProps}
            className={getClassNames([
                cnTooltip('popup'),
                popupProps.className,
                skinClass,
                color === 'inverted' ? invertedTheme : '',
                stackClasses,
            ])}
            onClose={onClose}
            isModal={isModal ?? false}
            isDismissable={isDismissable ?? !hasClose}
            ariaType={'dialog'}
            hasTail={hasTail}
            tailClassName={getClassNames([cnTooltip('tail', { color }), tailClassName])}
            containerPadding={props.containerPadding ?? 0}
            maxWidth={maxWidth === 'auto' ? undefined : Number(maxWidth)}
            shouldResize={customContent ? true : 'width'}
            data-testid={dataTestId}
            isWithoutShadow={color === 'inverted'}
        >
            <VStack
                data-testid={`${dataTestId}.TooltipContent`}
                scrollable={Boolean(customContent)}
                className={cnTooltip(
                    {
                        color,
                        'max-width': maxWidth,
                    },
                    [sizeSkinClass],
                )}
            >
                {isDesktop() && imageSrc && (
                    <>
                        <Image
                            width={'auto'}
                            height={'100%'}
                            src={imageSrc}
                            alt={imageAlt || ''}
                            className={cnTooltip('image')}
                        />
                        {hasClose && (
                            <FloatingButton
                                icon={IconClose}
                                size={'2xs'}
                                color={'contrast'}
                                className={cnTooltip('imageCloseButton')}
                                onClick={onClose}
                                title={closeButtonText}
                                data-testid={`${dataTestId}.CloseButton`}
                            />
                        )}
                    </>
                )}
                <VStack className={cnTooltip('content', { 'has-icon': Boolean(iconLeft) })}>{content}</VStack>
            </VStack>
        </Popup>
    );

    const wrappedPopup = tooltipContainer ? (
        <ProvideContextPopupsContainer contextPopupsContainerRef={tooltipContainer}>
            {popup}
        </ProvideContextPopupsContainer>
    ) : (
        popup
    );

    return (
        <LogProvider>
            {isDesktop() ? (
                wrappedPopup
            ) : (
                <Modal
                    className={cnTooltip(
                        {
                            'has-icon': Boolean(iconLeft),
                        },
                        [popupProps.className, skinClass],
                    )}
                    onClose={onClose}
                    isOpen={popupProps.isOpen}
                    bottomButtons={
                        !props.hideMobileBottomButton && (
                            <Button
                                onClick={onClose}
                                size={'m'}
                                color={'contrast'}
                                data-testid={`${dataTestId}.OkButton`}
                            >
                                {i18n('Понятно')}
                            </Button>
                        )
                    }
                    data-testid={dataTestId}
                >
                    {content}
                </Modal>
            )}
        </LogProvider>
    );
};

export const TooltipContainerContext = createContext<RefObject<HTMLElement> | null>(null);
